import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import InAppNav from "../../components/app/InAppNav";
import { ArrowRight, Loader2, Tag, Users, Clock, CalendarClock, UtensilsCrossed, Info, MapPin, XCircle, Maximize2, Navigation, Car, Package, Home, Briefcase, Pencil, Trash2, Settings2, Snowflake, RefreshCw, Star, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PageHeader, Section, StatusBadge } from "../../components/app/ui";
import ProfileReminder from "../../components/app/ProfileReminder";
import PlaceInput, { locateMe, reverseGeocode } from "../../components/app/PlaceInput";
import { SmartTag } from "../../components/app/SmartDefaults";
import { recall, remember } from "../../lib/smartDefaults";
import LiveRideMap from "../../components/app/LiveRideMap";
import RideMap from "../../components/app/RideMap";
import PickupFeeInfo from "../../components/app/PickupFeeInfo";
import DriverEtaBar from "../../components/app/DriverEtaBar";
import { ActiveRidePanel } from "../../components/app/RideWidgets";
import LocationPicker from "../../components/app/LocationPicker";
import ManageAddressesDialog from "../../components/app/ManageAddressesDialog";
import { LaunchOfferDialog } from "../../components/app/LaunchOfferDialog";
import { playChime, unlockAudio } from "../../lib/chime";
import CancelReasonDialog from "../../components/app/CancelReasonDialog";
import SearchingPulse from "../../components/app/SearchingPulse";
import OrderProgress, { EtaCountdown } from "../../components/app/OrderProgress";
import { ridesApi, foodApi, privateCarApi, customerApi, configApi, addressApi, inr, titleCase, errorMessage } from "../../lib/api";
import { paymentsApi, openCheckout } from "../../lib/razorpay";
import { useAuth } from "../../context/AuthContext";
import { DEFAULT_CENTER } from "../../lib/constants";

const ACTIVE_KEY = "kt_active_ride";
const VEHICLE_LABELS = { scooter: "Scooter", auto: "Autorickshaw", car: "Taxi Car", jumbo: "Jumbo Taxi" };

// Google Static Maps thumbnail showing the driver (green) heading to the delivery pin (coral).
function orderMapUrl(loc) {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key || !loc || loc.lat == null || loc.delivery_lat == null) return null;
  const drv = `${loc.lat},${loc.lng}`;
  const dst = `${loc.delivery_lat},${loc.delivery_lng}`;
  return `https://maps.googleapis.com/maps/api/staticmap?size=600x180&scale=2`
    + `&markers=${encodeURIComponent("color:0x10B981|label:D|" + drv)}`
    + `&markers=${encodeURIComponent("color:0xF43F5E|" + dst)}`
    + `&path=${encodeURIComponent("color:0x10B981AA|weight:3|" + drv + "|" + dst)}`
    + `&key=${key}`;
}

// Metres between two lat/lng points (Haversine).
function distanceM(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export default function BookRide() {
  const { user } = useAuth();
  const [pickup, setPickup] = useState(null);
  const [drop, setDrop] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [recentDrops, setRecentDrops] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const refreshAddresses = useCallback(() => addressApi.list().then((l) => setSavedAddresses((l || []).filter((a) => Number.isFinite(a.lat)))).catch(() => {}), []);
  const [estimate, setEstimate] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [ac, setAc] = useState(false);
  const [promo, setPromo] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [navDest, setNavDest] = useState(null);
  const [heatPoints, setHeatPoints] = useState([]);
  const [pickupSug, setPickupSug] = useState(null);
  const [sugDismissed, setSugDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ride, setRide] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [justAssigned, setJustAssigned] = useState(false);
  const [searchExpired, setSearchExpired] = useState(false);
  const prevRideStatus = useRef(null);

  // Search Timeout: flip to a clear retry/schedule choice once the search window elapses
  // with no driver accepting. (Backend now serializes times as UTC so this is accurate.)
  useEffect(() => {
    if (!ride || !["pending", "searching", "dispatching", "requested", "finding_driver"].includes(ride.status)) { setSearchExpired(false); return; }
    const exp = ride.search_expires_at ? new Date(ride.search_expires_at).getTime() : new Date(ride.created_at).getTime() + 300000;
    const check = () => setSearchExpired(Date.now() > exp);
    check();
    const t = setInterval(check, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ride?.status, ride?.search_expires_at, ride?.ride_id]);

  // Driver Found: chime + celebratory banner the moment a driver accepts a searching ride.
  useEffect(() => {
    const prev = prevRideStatus.current;
    const st = ride?.status || null;
    prevRideStatus.current = st;
    const searching = ["pending", "searching", "dispatching", "requested", "finding_driver", "fee_offer"];
    if (st === "driver_assigned" && prev && searching.includes(prev)) {
      try { playChime(); } catch { /* ignore */ }
      setJustAssigned(true);
      const t = setTimeout(() => setJustAssigned(false), 12000);
      return () => clearTimeout(t);
    }
    // Arrival: chime again the moment the driver reaches the pickup point.
    if (st === "driver_arriving" && prev === "driver_assigned") {
      try { playChime(); } catch { /* ignore */ }
      toast.success("Your driver has arrived at the pickup point");
    }
    if (st && st !== "driver_assigned") setJustAssigned(false);
  }, [ride?.status]);
  const [loadingRide, setLoadingRide] = useState(true);
  const [payMethod, setPayMethod] = useState(() => (recall(user?.user_id, "ridePay") === "razorpay" ? "razorpay" : "cash"));
  const [pickupAuto, setPickupAuto] = useState(null); // "location" | "last" | null
  const [pickupNote, setPickupNote] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [dropPinOpen, setDropPinOpen] = useState(false);
  const [pickupPinned, setPickupPinned] = useState(false);
  const [pinNudge, setPinNudge] = useState(false);
  const [widening, setWidening] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [acPromptDue, setAcPromptDue] = useState(false);
  const [mode, setMode] = useState("now"); // "now" | "later"
  const [isIntercity, setIsIntercity] = useState(false);
  const [roundTrip, setRoundTrip] = useState(false);
  const dropSourceRef = useRef("manual"); // "saved" | "recent" | "manual" — drives Smart Mode Suggest
  const [payFull, setPayFull] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const pickupOrigin = useRef(null); // the chosen "From" — anchors the 300 m pin area

  const minSchedule = useMemo(() => {
    const d = new Date(Date.now() + 16 * 60000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);

  // Smart default: pickup = current location, else last used pickup
  useEffect(() => {
    let alive = true;
    const useLast = () => {
      const last = recall(user?.user_id, "lastPickup");
      if (alive && last) { setPickup(last); setPickupAuto("last"); pickupOrigin.current = last; }
    };
    locateMe(6000).then((c) => {
      if (!alive) return;
      // Show the pin immediately; resolve the human-readable name in the background.
      setPickup({ name: "Current location", ...c }); setPickupAuto("location"); pickupOrigin.current = { name: "Current location", ...c };
      reverseGeocode(c.lat, c.lng).then((name) => {
        if (!alive || !name) return;
        setPickup((prev) => (prev ? { ...prev, name } : prev));
        if (pickupOrigin.current) pickupOrigin.current = { ...pickupOrigin.current, name };
      }).catch(() => {});
    }).catch(useLast);
    return () => { alive = false; };
  }, [user?.user_id]);

  // Rebook: prefill pickup + drop from a past ride (navigated with { state: { rebook } })
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const rb = location.state?.rebook;
    if (!rb) return;
    if (rb.pickup?.lat != null) { setPickup(rb.pickup); setPickupAuto(null); pickupOrigin.current = rb.pickup; }
    if (rb.drop?.lat != null) { setDrop(rb.drop); }
    window.history.replaceState({}, "");
    toast.success("Rebooking your last trip — confirm the fare to request");
  }, [location.state]);

  // Restore an active ride
  useEffect(() => {
    const id = localStorage.getItem(ACTIVE_KEY);
    if (!id) { setLoadingRide(false); return; }
    ridesApi.get(id).then((r) => {
      if (["completed", "cancelled"].includes(r.status) && (r.payment_status === "paid" || r.status === "cancelled")) localStorage.removeItem(ACTIVE_KEY);
      else setRide(r);
    }).catch(() => localStorage.removeItem(ACTIVE_KEY)).finally(() => setLoadingRide(false));
  }, []);

  // Real nearby drivers (online, verified, within 5 km) — exact distance + ETA; live-refreshed
  useEffect(() => {
    const c = pickup || DEFAULT_CENTER;
    const load = () => customerApi.nearbyDrivers(c.lat, c.lng).then(setDrivers).catch(() => setDrivers([]));
    const loadHeat = () => configApi.driverHeatmap(c.lat, c.lng, 8).then((d) => setHeatPoints(d.points || [])).catch(() => setHeatPoints([]));
    load(); loadHeat();
    const t = setInterval(() => { load(); loadHeat(); }, 10000);
    return () => clearInterval(t);
  }, [pickup]);

  // Pickup Suggest: if the chosen pickup likely incurs a far-pickup fee, suggest a nearby spot.
  useEffect(() => {
    if (!pickup || ride) { setPickupSug(null); return; }
    setSugDismissed(false);
    configApi.pickupSuggestion(pickup.lat, pickup.lng)
      .then((d) => setPickupSug(d.suggested ? d : null))
      .catch(() => setPickupSug(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup?.lat, pickup?.lng, ride]);

  // Fare estimate
  useEffect(() => {
    setEstimate(null); setVehicle(null);
    if (!pickup || !drop) return;
    let alive = true;
    setBusy(true);
    ridesApi.estimate({ pickup_lat: pickup.lat, pickup_lng: pickup.lng, drop_lat: drop.lat, drop_lng: drop.lng, is_intercity: isIntercity, round_trip: isIntercity ? roundTrip : false })
      .then((e) => {
        if (!alive) return;
        setEstimate(e);
        // Smart Mode Suggest: if a SAVED/RECENT drop turns out to be in another city, auto-switch
        // to Intercity (keeping pickup, drop & round-trip) instead of showing the block banner.
        if (e.intercity_required && !isIntercity && (dropSourceRef.current === "saved" || dropSourceRef.current === "recent")) {
          dropSourceRef.current = "manual";  // one-shot: don't fight a later manual City switch
          setIsIntercity(true);
          toast.info(`Switched to Intercity${e.drop_city ? ` for ${e.drop_city}` : ""} — this saved drop is in another city.`);
          return;
        }
        setVehicle(e.options?.[0]?.vehicle_type || null);
      })
      .catch((e) => toast.error(errorMessage(e, "Could not estimate fare")))
      .finally(() => alive && setBusy(false));
    return () => { alive = false; };
  }, [pickup, drop, isIntercity, roundTrip]);

  useEffect(() => { refreshAddresses(); addressApi.recentDrops().then(setRecentDrops).catch(() => {}); }, [refreshAddresses]);

  const savedCoordSet = useMemo(() => new Set(savedAddresses.map((a) => `${a.lat.toFixed(4)},${a.lng.toFixed(4)}`)), [savedAddresses]);
  const dropIsSaved = drop && savedCoordSet.has(`${Number(drop.lat).toFixed(4)},${Number(drop.lng).toFixed(4)}`);
  const hasLabel = (lbl) => savedAddresses.some((a) => (a.label || "").toLowerCase() === lbl.toLowerCase());
  const saveDropAs = async (label) => {
    if (!drop || !Number.isFinite(drop.lat)) return;
    try {
      await addressApi.add({ label, address: drop.name || label, lat: drop.lat, lng: drop.lng });
      await refreshAddresses();
      toast.success(`Saved as ${label}`);
    } catch (e) { toast.error(errorMessage(e, "Could not save address")); }
  };

  // Upcoming (scheduled) rides
  const [upcoming, setUpcoming] = useState([]);
  const loadUpcoming = useCallback(() => {
    ridesApi.scheduled().then((rows) => setUpcoming((rows || []).filter((s) => s.status === "pending" || s.status === "dispatching"))).catch(() => {});
  }, []);
  useEffect(() => { loadUpcoming(); }, [loadUpcoming]);
  const cancelUpcoming = async (id) => {
    try { await ridesApi.deleteScheduled(id); toast.success("Scheduled ride cancelled"); loadUpcoming(); }
    catch (e) { toast.error(errorMessage(e, "Could not cancel")); }
  };

  const startScheduleNow = async (id) => {
    try {
      const r = await ridesApi.dispatchScheduleNow(id);
      toast.success("Driver found — starting your ride now");
      loadUpcoming();
      if (r?.ride_id) { setRide(await ridesApi.get(r.ride_id)); localStorage.setItem(ACTIVE_KEY, r.ride_id); }
    } catch (e) { toast.error(errorMessage(e, "Couldn't start the ride")); }
  };

  // Live now — active food orders + private-car bookings
  const [liveOrders, setLiveOrders] = useState([]);
  const [livePc, setLivePc] = useState([]);
  const [orderLocs, setOrderLocs] = useState({});
  const loadLive = useCallback(() => {
    const FOOD_ACTIVE = ["placed", "finding_partner", "accepted", "preparing", "ready", "out_for_delivery", "awaiting_payment"];
    const PC_ACTIVE = ["requested", "confirmed", "pending_payment"];
    foodApi.orders().then(async (o) => {
      const act = (o || []).filter((x) => FOOD_ACTIVE.includes(x.status));
      setLiveOrders(act);
      const locs = {};
      await Promise.all(act.filter((x) => x.status === "out_for_delivery").map(async (x) => {
        try { locs[x.order_id] = await foodApi.driverLocation(x.order_id); } catch { /* ignore */ }
      }));
      setOrderLocs(locs);
    }).catch(() => {});
    privateCarApi.mine().then((b) => setLivePc((b || []).filter((x) => PC_ACTIVE.includes(x.status)))).catch(() => {});
  }, []);
  useEffect(() => { loadLive(); const t = setInterval(loadLive, 12000); return () => clearInterval(t); }, [loadLive]);
  const autoRebook10 = async () => {
    if (!ride) return;
    try {
      const when = new Date(Date.now() + 10 * 60 * 1000).toISOString();
      await ridesApi.schedule({
        pickup_lat: ride.pickup_lat, pickup_lng: ride.pickup_lng,
        drop_lat: ride.drop_lat, drop_lng: ride.drop_lng,
        pickup_address: ride.pickup_address, drop_address: ride.drop_address,
        vehicle_type: ride.vehicle_type || "car", scheduled_at: when,
        ac: !!ride.ac, is_intercity: !!ride.is_intercity, purpose: ride.purpose || "ride", fallback: true,
      });
      toast.success("Got it — we'll automatically rebook this in 10 minutes");
      try { await ridesApi.cancel(ride.ride_id); } catch { /* ignore */ }
      setRide(null); localStorage.removeItem(ACTIVE_KEY);
    } catch (e) { toast.error(errorMessage(e, "Could not schedule the rebooking")); }
  };
  const doCancelRide = async (reason, note) => {
    if (!ride) return;
    try {
      await ridesApi.cancel(ride.ride_id, reason ? { reason, note } : undefined);
      toast.success("Ride cancelled");
      setRide(null);
      localStorage.removeItem(ACTIVE_KEY);
    } catch (err) { toast.error(errorMessage(err, "Could not cancel the ride")); }
    finally { setCancelOpen(false); }
  };
  const openEditDrop = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    navigate("/app/trip?edit=drop");
  };

  const cancelActiveRide = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!ride) return;
    const assigned = ride.driver && !["searching", "fee_offer"].includes(ride.status);
    if (assigned) setCancelOpen(true);  // ask why so the assigned driver gets context
    else doCancelRide();                // no driver yet — no reason needed
  };

  const widenSearch = async () => {
    if (!ride) return;
    setWidening(true);
    try {
      await ridesApi.widen(ride.ride_id);
      toast.success("Searching farther — reaching drivers up to 8 km away");
    } catch (e) { toast.error(errorMessage(e, "Couldn't widen the search")); }
    finally { setWidening(false); }
  };

  const broadcastAllCars = async () => {
    if (!ride) return;
    setBroadcasting(true);
    try {
      const r = await ridesApi.broadcastAll(ride.ride_id);
      setRide(r);
      toast.success("Sent to all cars — if a non-AC car accepts, no AC charge applies");
    } catch (e) { toast.error(errorMessage(e, "Couldn't broaden to all cars")); }
    finally { setBroadcasting(false); }
  };

  const acSearching = ride && ride.ac_requested && !ride.ac_broadcast_all && ["pending", "searching", "dispatching", "requested", "finding_driver"].includes(ride.status);
  useEffect(() => {
    setAcPromptDue(false);
    if (!acSearching) return;
    const t = setTimeout(() => setAcPromptDue(true), 20000);
    return () => clearTimeout(t);
  }, [acSearching, ride?.ride_id]);

  // No-driver fallback: nobody within 8 km — convert this pickup into a scheduled ride.
  const scheduleFallback = async () => {
    if (!ride) return;
    const when = new Date(Date.now() + 30 * 60000);
    try {
      await ridesApi.schedule({
        pickup_lat: ride.pickup_lat, pickup_lng: ride.pickup_lng,
        drop_lat: ride.drop_lat, drop_lng: ride.drop_lng,
        pickup_address: ride.pickup_address, drop_address: ride.drop_address,
        vehicle_type: ride.vehicle_type, scheduled_at: when.toISOString(),
        purpose: ride.purpose || "ride", fallback: true,
      });
      await ridesApi.cancel(ride.ride_id);
      setRide(null);
      localStorage.removeItem(ACTIVE_KEY);
      loadUpcoming();
      toast.success(`Scheduled for ${when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} — we'll find a driver closer to pickup time`);
    } catch (e) { toast.error(errorMessage(e, "Couldn't schedule this pickup")); }
  };

  const selected = useMemo(() => estimate?.options?.find((o) => o.vehicle_type === vehicle), [estimate, vehicle]);
  const acPct = estimate?.ac_pct || 20;
  const acEligible = !!selected && ["car", "jumbo"].includes(selected.vehicle_type);
  const acAmount = acEligible && ac ? Math.round(selected.fare * acPct) / 100 : 0;
  const rideTotal = (selected ? (promoInfo ? promoInfo.final_amount : selected.fare) : 0) + acAmount;

  const applyPromo = async () => {
    if (!promo.trim() || !selected) return;
    try {
      const res = await foodApi.validatePromo({ code: promo.trim().toUpperCase(), order_type: "ride", subtotal: selected.fare });
      if (res.ok === false) throw new Error(res.message || "Invalid promo");
      setPromoInfo(res);
      toast.success(`Promo applied: ${inr(res.discount)} off`);
    } catch (e) {
      setPromoInfo(null);
      toast.error(errorMessage(e, "Promo code not valid"));
    }
  };

  const clearSim = () => {};
  useEffect(() => () => {}, []);

  const useMyLocation = async () => {
    try {
      const c = await locateMe(6000);
      const p = { name: "Current location", ...c };
      setPickup(p); setPickupAuto("location"); pickupOrigin.current = p; setPickupPinned(false); setPinOpen(true);
      toast.success("Using your current location as pickup");
      // Fill the human-readable name in the background so the button never hangs on geocoding.
      reverseGeocode(c.lat, c.lng).then((name) => {
        if (!name) return;
        setPickup((prev) => (prev ? { ...prev, name } : prev));
        if (pickupOrigin.current) pickupOrigin.current = { ...pickupOrigin.current, name };
      }).catch(() => {});
    } catch {
      toast.error("Couldn't get your location — please allow location access");
    }
  };

  const book = async () => {
    if (!pickup || !drop || !selected) return;
    try { unlockAudio(); } catch { /* ignore */ }  // enable the driver-found chime (needs a user gesture)
    if (!pickupPinned) { setPinOpen(true); setPinNudge(true); setTimeout(() => setPinNudge(false), 2400); toast.error("Please set your exact pickup pin (within 300 m) before booking"); return; }
    if (distanceM(pickupOrigin.current, pickup) > 300) { setPinOpen(true); toast.error("Your pickup pin must be within 300 m of your location"); return; }
    if (mode === "later" && !scheduledAt) { toast.error("Pick a date and time for your ride"); return; }
    setBusy(true);
    try {
      if (mode === "later") {
        await ridesApi.schedule({ pickup_lat: pickup.lat, pickup_lng: pickup.lng, drop_lat: drop.lat, drop_lng: drop.lng, pickup_address: pickup.name, drop_address: drop.name, vehicle_type: selected.vehicle_type, scheduled_at: new Date(scheduledAt).toISOString(), notes: pickupNote.trim() || null, promo_code: promoInfo ? promoInfo.code : null, is_intercity: isIntercity, round_trip: isIntercity ? roundTrip : false, ac: acEligible ? ac : false });
        remember(user?.user_id, "lastPickup", pickup);
        remember(user?.user_id, "lastDrop", drop);
        toast.success("Ride scheduled!", { description: "We'll assign a driver close to your pickup time. See it under Scheduled rides." });
        loadUpcoming();
        reset();
        return;
      }
      const r = await ridesApi.book({ pickup_lat: pickup.lat, pickup_lng: pickup.lng, drop_lat: drop.lat, drop_lng: drop.lng, pickup_address: pickup.name, drop_address: drop.name, vehicle_type: selected.vehicle_type, promo_code: promoInfo ? promoInfo.code : null, payment_method: payMethod, pickup_note: pickupNote.trim() || null, is_intercity: isIntercity, round_trip: isIntercity ? roundTrip : false, pay_full: isIntercity ? payFull : false, ac: acEligible ? ac : false });
      // Intercity bookings need an upfront ONLINE payment: a 10% non-refundable deposit
      // (default) or the full fare in advance. Pay it before the ride starts searching.
      if (r.status === "awaiting_payment" && r.is_intercity) {
        const dueNow = r.amount_due_now != null ? r.amount_due_now : r.fare;
        try {
          const order = await paymentsApi.createRideOrder(r.ride_id);
          const res = await openCheckout({ order, amount: dueNow, user, description: r.prepaid_full ? "Intercity fare (paid in advance)" : "Intercity 10% booking deposit" });
          const verified = await paymentsApi.verifyRide({ ride_id: r.ride_id, razorpay_order_id: res.razorpay_order_id || order.order_id || `web_${r.ride_id}`, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature || null, method: "online" });
          setRide(verified);
          localStorage.setItem(ACTIVE_KEY, r.ride_id);
          remember(user?.user_id, "lastPickup", pickup);
          remember(user?.user_id, "lastDrop", drop);
          remember(user?.user_id, "lastVehicle", selected.vehicle_type);
          toast.success(r.prepaid_full ? "Payment received! Looking for a nearby driver…" : `Deposit of ${inr(dueNow)} received! Looking for a nearby driver…`);
          if (!r.prepaid_full) toast.info("Your deposit is deducted from the final fare — you'll only pay the balance to your driver.", { duration: 7000 });
        } catch (e) {
          try { await ridesApi.cancel(r.ride_id); } catch { /* ignore */ }
          toast.error(e?.cancelled ? "Booking cancelled — deposit not paid" : errorMessage(e, "Payment could not be completed. You weren't charged — please try again."));
        }
        setBusy(false);
        return;
      }
      setRide(r);
      localStorage.setItem(ACTIVE_KEY, r.ride_id);
      remember(user?.user_id, "lastPickup", pickup);
      remember(user?.user_id, "lastDrop", drop);
      remember(user?.user_id, "lastVehicle", selected.vehicle_type);
      remember(user?.user_id, "ridePay", payMethod);
      toast.success("Looking for a nearby driver…");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "intercity_required") {
        toast.error(d.message || "Please switch to Intercity mode for this trip.");
        setIsIntercity(true);
      } else {
        toast.error(errorMessage(e, "Could not book ride"));
      }
    } finally {
      setBusy(false);
    }
  };

  const onRideChange = useCallback((r) => {
    setRide(r);
  }, []);

  const reset = () => {
    localStorage.removeItem(ACTIVE_KEY);
    setRide(null); setPickup(null); setDrop(null); setEstimate(null); setPromo(""); setPromoInfo(null); setPickupNote(""); setPinOpen(false); setPickupPinned(false); pickupOrigin.current = null;
  };

  const firstName = user?.name?.split(" ")[0] || "there";

  return (
    <>
      <LaunchOfferDialog />
      <PageHeader
        title={`Where to, ${firstName}?`}
        description="Book a ride anywhere in India. Fares are shown before you confirm."
        actions={
          <>
            <Button asChild variant="outline" className="rounded-full"><Link to="/app/schedule" data-testid="link-schedule"><CalendarClock className="mr-2 h-4 w-4" /> Schedule a ride</Link></Button>
            <Button asChild variant="outline" className="rounded-full"><Link to="/app/food" data-testid="link-food"><UtensilsCrossed className="mr-2 h-4 w-4" /> Order food</Link></Button>
          </>
        }
      />
      <div className="mx-auto mb-6 max-w-2xl"><ProfileReminder /></div>
      <ManageAddressesDialog open={manageOpen} onOpenChange={setManageOpen} onChanged={refreshAddresses} />
      {(ride || liveOrders.length > 0 || livePc.length > 0) && (
        <div className="mx-auto mb-6 max-w-2xl rounded-2xl border border-surface-300 bg-white p-4" data-testid="home-live-orders">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-bold text-ink">Live now</h2>
            <Link to="/app/orders" className="text-xs font-bold text-brand" data-testid="home-live-see-all">My orders</Link>
          </div>
          <div className="space-y-2">
            {ride && !["completed", "cancelled"].includes(ride.status) && (
              <Link to="/app/trip" className="block rounded-xl border border-surface-200 p-3 transition hover:border-brand-200" data-testid="home-live-ride">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand"><Car className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-ink">Ride · {ride.drop_address || "Your destination"}</span>
                    <span className="block text-xs text-ink-soft">{titleCase(ride.vehicle_type || "")} · {inr(ride.fare)}{ride.distance_km ? ` · ${ride.distance_km} km` : ""}</span>
                  </span>
                  <StatusBadge status={ride.status} />
                </div>
                {["searching", "fee_offer"].includes(ride.status) && (
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-sunny-50 px-2 py-1 text-xs font-bold text-sunny-700" data-testid="home-live-ride-searching"><Clock className="h-3.5 w-3.5" /> Searching for a driver…</span>
                    <button type="button" onClick={cancelActiveRide} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold text-red-600 hover:bg-red-50" data-testid="home-live-ride-cancel"><XCircle className="h-3.5 w-3.5" /> Cancel</button>
                  </div>
                )}
                {ride.eta_min != null && ["driver_assigned", "driver_arriving", "in_progress"].includes(ride.status) && (
                  <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-brand-50 px-2 py-1 text-xs font-bold text-brand" data-testid="home-live-ride-eta"><Clock className="h-3.5 w-3.5" />{ride.status === "in_progress" ? `Arriving at drop in ~${ride.eta_min} min` : `Driver ~${ride.eta_min} min away`}</p>
                )}
                {ride.purpose !== "goods" && ["driver_assigned", "driver_arriving", "in_progress"].includes(ride.status) && (
                  <div className="mt-3 flex items-center gap-2 border-t border-surface-200 pt-3">
                    <button type="button" onClick={openEditDrop} className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs font-bold text-white hover:bg-brand-600" data-testid="home-live-ride-change-drop"><Navigation className="h-3.5 w-3.5" /> Change drop</button>
                    <span className="text-xs text-ink-muted">Fare updates automatically</span>
                  </div>
                )}
              </Link>
            )}
            {liveOrders.map((o) => (
              <Link key={o.order_id} to={`/app/orders/${o.order_id}`} className="block rounded-xl border border-surface-200 p-3 transition hover:border-brand-200" data-testid={`home-live-order-${o.order_id}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand"><UtensilsCrossed className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-ink">{o.restaurant_name || "Food order"}</span>
                    <span className="block text-xs text-ink-soft">{(o.items?.length || o.items_count || 0)} item{(o.items?.length || o.items_count || 0) === 1 ? "" : "s"} · {inr(o.total || 0)}</span>
                  </span>
                  {["accepted", "preparing", "ready", "out_for_delivery"].includes(o.status) && o.eta_min != null ? <EtaCountdown etaMin={o.eta_min} testId={`home-live-order-eta-${o.order_id}`} /> : <StatusBadge status={o.status} />}
                </div>
                <div className="mt-3"><OrderProgress status={o.status} testId={`home-live-progress-${o.order_id}`} /></div>
                {["placed", "finding_partner", "awaiting_payment"].includes(o.status) && (
                  <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-sunny-50 px-2 py-1 text-xs font-bold text-sunny-700" data-testid={`home-live-waiting-${o.order_id}`}>
                    <Clock className="h-3.5 w-3.5" /> Waiting for the restaurant to accept…
                  </p>
                )}
                {o.status === "out_for_delivery" && orderMapUrl(orderLocs[o.order_id]) && (
                  <img src={orderMapUrl(orderLocs[o.order_id])} alt="Driver approaching your delivery" className="mt-3 h-24 w-full rounded-lg border border-surface-200 object-cover" data-testid={`home-live-map-${o.order_id}`} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
              </Link>
            ))}
            {livePc.map((b) => (
              <Link key={b.booking_id} to="/app/private-cars" className="flex items-center gap-3 rounded-xl border border-surface-200 p-3 transition hover:border-brand-200" data-testid={`home-live-pc-${b.booking_id}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand"><Car className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-ink">Private car{b.driver_name ? ` · ${b.driver_name}` : ""}</span>
                  <span className="block text-xs text-ink-soft">{new Date(b.start_date).toLocaleDateString([], { day: "numeric", month: "short" })} · {b.days} day{b.days > 1 ? "s" : ""}</span>
                </span>
                <StatusBadge status={b.status} />
              </Link>
            ))}
          </div>
        </div>
      )}
      {!ride && (
        <Link to="/app/private-cars" data-testid="hire-private-driver" className="mx-auto mb-6 flex max-w-2xl items-center gap-4 rounded-2xl border border-graphite-600 bg-graphite-700 p-4 text-white transition hover:bg-graphite-600">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sunny text-graphite-700"><Car className="h-6 w-6" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold">Hire Your Private Driver</span>
            <span className="block text-xs text-white/70">Book a chauffeur with their car by the day — pick a destination and send requests to multiple drivers.</span>
          </span>
          <ArrowRight className="h-5 w-5 shrink-0 text-sunny" />
        </Link>
      )}
      {!ride && (
        <Link to="/app/goods" data-testid="goods-delivery-card" className="mx-auto mb-6 flex max-w-2xl items-center gap-4 rounded-2xl border border-graphite-600 bg-graphite-700 p-4 text-white transition hover:bg-graphite-600">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sunny text-graphite-700"><Package className="h-6 w-6" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold">Goods Delivery</span>
            <span className="block text-xs text-white/70">Send a package by Bike/Scooter (up to 20 kg) or Autorickshaw (up to 50 kg). You pack it, we deliver it.</span>
          </span>
          <ArrowRight className="h-5 w-5 shrink-0 text-sunny" />
        </Link>
      )}
      {!ride && (estimate?.cancel_balance ?? user?.cancel_balance) > 0 && (
        <div className="mx-auto mb-6 flex max-w-2xl items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="cancel-balance-note">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>You have a ₹{estimate?.cancel_balance ?? user.cancel_balance} cancellation fee outstanding. It will be added to your next ride fare.</span>
        </div>
      )}
      <div className={ride && ride.status !== "cancelled" ? "grid gap-6 lg:grid-cols-[420px_1fr]" : "mx-auto max-w-2xl space-y-6"}>
        <div className="space-y-6">
          {loadingRide ? (
            <div className="kt-card h-64 animate-pulse bg-surface-200" />
          ) : ride ? (
            <Section title="Your ride">
              {["pending", "searching", "dispatching", "requested", "finding_driver"].includes(ride.status) ? (
                searchExpired ? (
                  <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-center" data-testid="ride-search-timeout">
                    <p className="text-base font-bold text-ink">No driver accepted in time</p>
                    <p className="mt-1 text-sm text-ink-soft">All nearby drivers are busy right now. You can try again, or schedule this pickup for a bit later.</p>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                      <Button type="button" onClick={widenSearch} disabled={widening} className="flex-1 rounded-full bg-brand text-white hover:bg-brand-600" data-testid="timeout-try-again">
                        {widening ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Maximize2 className="mr-2 h-4 w-4" />} Try again (search wider)
                      </Button>
                      {(ride.purpose || "ride") !== "goods" && (
                        <Button type="button" variant="outline" onClick={scheduleFallback} className="flex-1 rounded-full" data-testid="timeout-schedule"><CalendarClock className="mr-2 h-4 w-4" /> Schedule for later</Button>
                      )}
                    </div>
                    {(ride.purpose || "ride") !== "goods" && (
                      <Button type="button" onClick={autoRebook10} className="mt-2 w-full rounded-full bg-graphite text-white hover:bg-graphite-600" data-testid="timeout-auto-rebook"><RefreshCw className="mr-2 h-4 w-4" /> Auto-rebook in 10 minutes</Button>
                    )}
                    <button type="button" onClick={cancelActiveRide} className="mt-2 text-xs font-semibold text-red-600 hover:underline" data-testid="timeout-cancel">Cancel this request</button>
                  </div>
                ) : (
                <>
                  {(() => {
                    const reached = ride.dispatch_reached || 0;
                    return reached > 0 ? (
                      <SearchingPulse title="A driver is reviewing your request…" subtitle={`${reached} driver${reached > 1 ? "s" : ""} nearby ${reached > 1 ? "have" : "has"} your request — waiting for one to accept. Hang tight!`} icon={<Car className="h-6 w-6" />} testId="ride-searching-pulse" />
                    ) : (
                      <SearchingPulse title="Finding a driver near you…" subtitle="No drivers within 5 km yet — hang tight. We're widening the search to reach more drivers." icon={<Car className="h-6 w-6" />} testId="ride-searching-pulse" />
                    );
                  })()}
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <Button type="button" variant="outline" onClick={widenSearch} disabled={widening || ride.force_wide} className="flex-1 rounded-full border-brand text-brand hover:bg-brand-50" data-testid="search-wider-now">
                      {widening ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Maximize2 className="mr-2 h-4 w-4" />}{ride.force_wide ? "Searching wider (8 km)" : "Search wider now"}
                    </Button>
                    <Button type="button" variant="ghost" onClick={cancelActiveRide} className="rounded-full text-red-600 hover:bg-red-50" data-testid="searching-cancel"><XCircle className="mr-2 h-4 w-4" /> Cancel</Button>
                  </div>
                  {acSearching && (
                    <div className={`mt-3 rounded-xl border px-3 py-3 ${acPromptDue ? "border-sky-300 bg-sky-50" : "border-surface-300 bg-surface-50"}`} data-testid="ac-fallback-box">
                      <p className="flex items-start gap-1.5 text-xs leading-relaxed text-ink-soft">
                        <Snowflake className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
                        <span>{acPromptDue ? "No AC car available nearby. Send this request to all cars instead?" : "Looking for AC cars only."} If a non-AC car accepts, the {acPct}% AC charge won't be applied.</span>
                      </p>
                      <Button type="button" onClick={broadcastAllCars} disabled={broadcasting} className="mt-2 w-full rounded-full bg-sky-600 text-white hover:bg-sky-700" data-testid="ac-broadcast-all">
                        {broadcasting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Car className="mr-2 h-4 w-4" />}Send to all cars (no AC charge)
                      </Button>
                    </div>
                  )}
                  {ride.force_wide && (ride.purpose || "ride") !== "goods" && (
                    <button type="button" onClick={scheduleFallback} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-full border border-dashed border-surface-400 px-3 py-2 text-xs font-semibold text-ink-soft hover:border-brand hover:text-brand" data-testid="schedule-fallback">
                      <CalendarClock className="h-4 w-4" /> No driver nearby? Schedule this pickup for later
                    </button>
                  )}
                </>
                )
              ) : null}
              {justAssigned && ride.driver && (
                <div className="mb-3 flex items-center gap-3 rounded-2xl border border-green-300 bg-green-50 p-3 animate-in fade-in slide-in-from-top-2" data-testid="driver-found-banner">
                  {ride.driver.photo ? (
                    <img src={ride.driver.photo} alt={ride.driver.name} className="h-12 w-12 shrink-0 rounded-full border-2 border-green-500 object-cover" data-testid="driver-found-photo" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-green-600 text-lg font-bold text-white">{(ride.driver.name || "D").charAt(0)}</span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-green-800">🎉 Driver found — {ride.driver.name}</p>
                    <p className="truncate text-xs text-green-700">{[ride.driver.vehicle || ride.vehicle_type, ride.driver.plate].filter(Boolean).join(" · ")}{ride.driver.eta_min ? ` · about ${ride.driver.eta_min} min away` : ""}</p>
                    {ride.driver.rating ? (
                      <p className="mt-0.5 inline-flex items-center gap-1 text-xs font-bold text-green-800" data-testid="driver-found-rating">
                        <Star className="h-3 w-3 fill-amber-400 text-amber-500" /> {ride.driver.rating}{ride.driver.trips ? <span className="font-semibold text-green-700"> · {ride.driver.trips.toLocaleString()} trips</span> : null}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-col gap-1.5">
                    {ride.driver.phone && <a href={`tel:${ride.driver.phone}`} data-testid="driver-found-call" className="flex items-center justify-center gap-1.5 rounded-full bg-green-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-green-700"><Phone className="h-3.5 w-3.5" /> Call</a>}
                    <Link to={`/app/chat/${ride.ride_id}`} data-testid="driver-found-message" className="flex items-center justify-center gap-1.5 rounded-full border border-green-400 bg-white px-3 py-1.5 text-xs font-bold text-green-800 hover:bg-green-100"><MessageCircle className="h-3.5 w-3.5" /> Message</Link>
                  </div>
                </div>
              )}
              <ActiveRidePanel ride={ride} onChange={onRideChange} onDone={reset} />
            </Section>
          ) : (
            <>
              {upcoming.length > 0 && (
                <Section title="Upcoming rides" description={`${upcoming.length} scheduled`}>
                  <div className="space-y-2" data-testid="upcoming-rides">
                    {upcoming.map((s) => (
                      <div key={s.schedule_id} className="rounded-2xl border border-surface-300 bg-white p-3" data-testid={`upcoming-${s.schedule_id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink"><CalendarClock className="h-4 w-4 text-brand" /> {new Date(s.scheduled_at).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                          {s.is_intercity && <span className="rounded-full bg-graphite px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">Intercity</span>}
                        </div>
                        <p className="mt-1.5 truncate text-xs text-ink-soft">{s.pickup_address} → {s.drop_address}</p>
                        {s.fallback && s.early_available && (
                          <button type="button" onClick={() => startScheduleNow(s.schedule_id)} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-full bg-green-600 px-3 py-2 text-xs font-bold text-white hover:bg-green-700" data-testid={`start-early-${s.schedule_id}`}>
                            <Car className="h-4 w-4" /> A driver's available now — start early?
                          </button>
                        )}
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-xs text-ink-muted">{VEHICLE_LABELS[s.vehicle_type] || s.vehicle_type} · est. {inr(s.estimated_fare)}</span>
                          <button type="button" onClick={() => cancelUpcoming(s.schedule_id)} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid={`cancel-upcoming-${s.schedule_id}`}><XCircle className="h-3.5 w-3.5" /> Cancel</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
              <Section title="Trip details">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-2" data-testid="ride-mode-toggles">
                    <div className="flex rounded-xl bg-surface-100 p-1">
                      {[{ id: "now", label: "Ride now" }, { id: "later", label: "Book for later" }].map((m) => (
                        <button key={m.id} type="button" onClick={() => setMode(m.id)} className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${mode === m.id ? "bg-white text-brand shadow-sm" : "text-ink-soft"}`} data-testid={`ride-mode-${m.id}`} aria-pressed={mode === m.id}>{m.label}</button>
                      ))}
                    </div>
                    <div className="flex rounded-xl bg-surface-100 p-1">
                      {[{ id: false, label: "City" }, { id: true, label: "Intercity" }].map((t) => (
                        <button key={String(t.id)} type="button" onClick={() => setIsIntercity(t.id)} className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${isIntercity === t.id ? "bg-white text-brand shadow-sm" : "text-ink-soft"}`} data-testid={`ride-trip-${t.id ? "intercity" : "city"}`} aria-pressed={isIntercity === t.id}>{t.label}</button>
                      ))}
                    </div>
                  </div>
                  {isIntercity && <p className="-mt-1 flex items-start gap-1.5 rounded-xl bg-brand-50 px-3 py-2 text-xs text-brand" data-testid="intercity-note"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Intercity trips run on Taxi Car or Jumbo Taxi only. Set your destination city as the drop.</p>}
                  {isIntercity && (
                    <div className="flex rounded-xl bg-surface-100 p-1" data-testid="ride-triptype-toggle">
                      {[{ id: false, label: "One way" }, { id: true, label: "Round trip" }].map((t) => (
                        <button key={String(t.id)} type="button" onClick={() => setRoundTrip(t.id)} className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${roundTrip === t.id ? "bg-white text-brand shadow-sm" : "text-ink-soft"}`} data-testid={`ride-triptype-${t.id ? "round" : "oneway"}`} aria-pressed={roundTrip === t.id}>{t.label}</button>
                      ))}
                    </div>
                  )}
                  {isIntercity && roundTrip && <p className="-mt-1 text-[11px] text-ink-muted" data-testid="roundtrip-note">Round trip adds a return-journey charge (return distance at half the per-km rate) so the driver isn't out of pocket coming back.</p>}
                  {mode === "later" && (
                    <div data-testid="schedule-field">
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-muted">Pickup date &amp; time</label>
                      <Input type="datetime-local" value={scheduledAt} min={minSchedule} onChange={(e) => setScheduledAt(e.target.value)} className="h-11 bg-white" data-testid="schedule-datetime" />
                      <p className="mt-1 text-xs text-ink-muted">We'll find a driver close to this time. Book at least 15 minutes ahead.</p>
                    </div>
                  )}
                  <PlaceInput label="Pickup" value={pickup} near={pickupOrigin.current} onChange={(p) => { setPickup(p); setPickupAuto(null); pickupOrigin.current = p; setPickupPinned(false); setPinOpen(true); }} placeholder="Search a place or landmark" allowCurrent testId="pickup-input"
                    labelExtra={pickup && pickupAuto ? <SmartTag testId="pickup-smart" changeLabel="Change" onChange={() => { setPickup(null); setPickupAuto(null); setPickupPinned(false); setTimeout(() => document.querySelector('[data-testid="pickup-input"]')?.focus(), 0); }} /> : null} />
                  {pickup && pickupAuto && <p className="-mt-2 text-xs text-ink-muted" data-testid="pickup-smart-hint">{pickupAuto === "location" ? "Using your current location as pickup." : "Using your last pickup point."}</p>}
                  <Button type="button" variant="outline" onClick={useMyLocation} className="w-full rounded-xl border-brand text-brand hover:bg-brand-50" data-testid="use-current-location"><Navigation className="mr-2 h-4 w-4" /> Use my current location</Button>
                  {pickupSug && !sugDismissed && (
                    <div className="flex items-start gap-2 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2.5" data-testid="pickup-suggestion">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-brand">{pickupSug.note}</p>
                        <div className="mt-1.5 flex gap-2">
                          <button type="button" onClick={() => { const p = { ...pickup, lat: pickupSug.lat, lng: pickupSug.lng }; pickupOrigin.current = p; setPickup(p); setPickupPinned(false); setPickupSug(null); toast.success("Pickup moved to a spot with drivers nearby"); }} className="rounded-full bg-brand px-3 py-1 text-[11px] font-bold text-white hover:bg-brand-600" data-testid="use-suggested-pickup">Use this spot</button>
                          <button type="button" onClick={() => setSugDismissed(true)} className="rounded-full px-3 py-1 text-[11px] font-semibold text-ink-soft hover:bg-surface-200" data-testid="dismiss-suggested-pickup">Keep mine</button>
                        </div>
                      </div>
                    </div>
                  )}
                  <PlaceInput label="Drop" value={drop} near={pickup} onChange={(p) => { dropSourceRef.current = "manual"; setDrop(p); if (p && Number.isFinite(p.lat)) setDropPinOpen(false); }} placeholder="Where are you going?" testId="drop-input" />
                  {drop && Number.isFinite(drop.lat) && (
                    <div>
                      <div className="mb-1.5 flex items-center justify-between">
                        <span className="text-xs text-ink-muted">Not exact? Drag the pin to fine-tune the drop.</span>
                        <button type="button" onClick={() => setDropPinOpen((v) => !v)} className="text-xs font-semibold text-brand" data-testid="toggle-drop-pin">{dropPinOpen ? "Hide map" : "Adjust on map"}</button>
                      </div>
                      {dropPinOpen && (
                        <div className="overflow-hidden rounded-xl border border-surface-300" data-testid="drop-map-picker">
                          <LocationPicker value={drop} onChange={(p) => setDrop({ ...drop, lat: p.lat, lng: p.lng })} />
                        </div>
                      )}
                    </div>
                  )}
                  {(savedAddresses.length > 0 || recentDrops.length > 0) && (
                    <div className="space-y-2" data-testid="drop-saved-addresses">
                      <div className="flex flex-wrap gap-2">
                        {savedAddresses.map((a) => {
                          const active = drop && Number(drop.lat).toFixed(4) === a.lat.toFixed(4) && Number(drop.lng).toFixed(4) === a.lng.toFixed(4);
                          const Ic = (a.label || "").toLowerCase() === "home" ? Home : (a.label || "").toLowerCase() === "work" ? Briefcase : MapPin;
                          return (
                            <button key={a.address_id} type="button" onClick={() => { dropSourceRef.current = "saved"; setDrop({ lat: a.lat, lng: a.lng, name: a.address }); }} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${active ? "border-brand bg-brand-50 text-brand" : "border-surface-300 text-ink-soft hover:border-brand-200"}`} data-testid={`drop-saved-${a.address_id}`}>
                              <Ic className="h-3.5 w-3.5" /> {a.label || a.address}
                            </button>
                          );
                        })}
                        {recentDrops.filter((r) => !savedCoordSet.has(`${r.lat.toFixed(4)},${r.lng.toFixed(4)}`)).map((r, i) => (
                          <button key={`rec-${i}`} type="button" onClick={() => { dropSourceRef.current = "recent"; setDrop({ lat: r.lat, lng: r.lng, name: r.address }); }} className="inline-flex max-w-[220px] items-center gap-1.5 rounded-full border border-surface-300 px-3 py-1.5 text-xs font-medium text-ink-soft hover:border-brand-200" data-testid={`drop-recent-${i}`}>
                            <Clock className="h-3.5 w-3.5 shrink-0 text-ink-muted" /> <span className="truncate">{r.address}</span>
                          </button>
                        ))}
                        {savedAddresses.length > 0 && (
                          <button type="button" onClick={() => setManageOpen(true)} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-surface-300 px-3 py-1.5 text-xs font-semibold text-ink-muted hover:border-brand-200" data-testid="manage-addresses-open">
                            <Settings2 className="h-3.5 w-3.5" /> Manage
                          </button>
                        )}
                      </div>
                      {drop && !dropIsSaved && (
                        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="save-drop-as">
                          <span className="text-ink-muted">Save this drop as</span>
                          {!hasLabel("Home") && <button type="button" onClick={() => saveDropAs("Home")} className="inline-flex items-center gap-1 rounded-full bg-surface-100 px-2.5 py-1 font-semibold text-ink-soft hover:bg-surface-200" data-testid="save-drop-home"><Home className="h-3 w-3" /> Home</button>}
                          {!hasLabel("Work") && <button type="button" onClick={() => saveDropAs("Work")} className="inline-flex items-center gap-1 rounded-full bg-surface-100 px-2.5 py-1 font-semibold text-ink-soft hover:bg-surface-200" data-testid="save-drop-work"><Briefcase className="h-3 w-3" /> Work</button>}
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-muted">Pickup note for driver (optional)</label>
                    <textarea value={pickupNote} onChange={(e) => setPickupNote(e.target.value)} rows={2} maxLength={200} placeholder="e.g. Near the blue gate, I'm in a red jacket" className="w-full rounded-xl border border-surface-300 bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand" data-testid="pickup-note" />
                  </div>
                </div>
              </Section>
              {(busy && !estimate) && <div className="kt-card flex items-center gap-3 p-5 text-sm text-ink-soft"><Loader2 className="h-4 w-4 animate-spin" /> Calculating fares…</div>}
              {estimate?.intercity_required && !isIntercity && (
                <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="intercity-required-banner">
                  <p className="flex items-center gap-2 text-sm font-semibold text-amber-900"><Info className="h-4 w-4" /> This looks like an intercity trip{estimate.drop_city ? ` to ${estimate.drop_city}` : ""}</p>
                  <p className="mt-1 text-xs text-amber-800">City rides can't drop in another city. Switch to Intercity to book this trip on a Taxi Car or Jumbo Taxi with intercity pricing.</p>
                  <Button type="button" onClick={() => { setIsIntercity(true); }} className="mt-3 rounded-full bg-amber-500 text-white hover:bg-amber-600" data-testid="switch-to-intercity">Switch to Intercity <ArrowRight className="ml-2 h-4 w-4" /></Button>
                </div>
              )}
              {estimate && !(estimate.intercity_required && !isIntercity) && (
                <Section title="Choose a ride" description={`${estimate.distance_km} km trip`}>
                  <div className="space-y-2" data-testid="vehicle-options">
                    {estimate.options.map((o) => {
                      const active = o.vehicle_type === vehicle;
                      return (
                        <button key={o.vehicle_type} type="button" onClick={() => { setVehicle(o.vehicle_type); setPromoInfo(null); }} className={`flex w-full items-center gap-4 rounded-2xl border p-3 text-left transition-colors duration-200 ${active ? "border-brand bg-brand-50" : "border-surface-300 bg-white hover:border-brand-200"}`} data-testid={`vehicle-${o.vehicle_type}`}>
                          {o.image ? <img src={o.image} alt={o.name} className="h-14 w-20 rounded-xl object-cover" /> : <span className="h-14 w-20 rounded-xl bg-surface-200" />}
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-ink">{o.name}</span>
                            <span className="mt-0.5 flex items-center gap-3 text-xs text-ink-soft"><span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{o.eta_min} min away</span><span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{o.seats}</span></span>
                          </span>
                          <span className="flex flex-col items-end">
                            <span className="text-base font-bold text-ink">{inr(o.fare)}</span>
                            {estimate.breakdowns?.[o.vehicle_type]?.gst_amount > 0 && <span className="text-[10px] text-ink-muted" data-testid={`vehicle-gst-${o.vehicle_type}`}>incl. {inr(estimate.breakdowns[o.vehicle_type].gst_amount)} GST</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <PickupFeeInfo className="mt-3" />
                  {selected && estimate.breakdowns?.[vehicle] && (() => {
                    const bd = estimate.breakdowns[vehicle];
                    return (
                      <div className="mt-3 rounded-2xl border border-surface-300 bg-white p-3 text-xs" data-testid="fare-breakdown">
                        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted">Fare breakdown</p>
                        <div className="space-y-1 text-ink-soft">
                          <div className="flex justify-between"><span>Base fare{isIntercity ? " (intercity)" : ""}</span><span className="font-medium text-ink">{inr(bd.base)}</span></div>
                          <div className="flex justify-between"><span>Distance · {bd.charged_km} km @ ₹{bd.per_km}/km{isIntercity ? ` (₹${bd.taper_per_km}/km beyond ${bd.taper_km} km)` : ""}</span><span className="font-medium text-ink">{inr(bd.distance_charge)}</span></div>
                          <div className="flex justify-between"><span>Time · {bd.duration_min} min @ ₹{bd.per_min}/min</span><span className="font-medium text-ink">{inr(bd.time_charge)}</span></div>
                          {bd.return_charge > 0 && <div className="flex justify-between"><span>Return journey (round trip)</span><span className="font-medium text-ink">{inr(bd.return_charge)}</span></div>}
                          {acAmount > 0 && <div className="flex justify-between"><span>Air Conditioning (+{acPct}%)</span><span className="font-medium text-ink">{inr(acAmount)}</span></div>}
                          {bd.surge_amount > 0 && <div className="flex justify-between"><span>Surge ({bd.surge_reasons?.join(", ") || "peak"})</span><span className="font-medium text-ink">{inr(bd.surge_amount)}</span></div>}
                          {bd.surge_amount > 0 && <p className="text-[11px] leading-snug text-amber-700" data-testid="surge-explainer">Surge applies due to {bd.surge_reasons?.join(" & ") || "peak hours"}. This extra amount goes entirely to your driver — KohimaTaxi takes no profit from surge.</p>}
                          {bd.gst_amount > 0 && <div className="flex justify-between"><span>GST ({bd.gst_pct}%)</span><span className="font-medium text-ink">{inr(bd.gst_amount)}</span></div>}
                          <div className="flex justify-between border-t border-surface-200 pt-1 text-sm font-bold text-ink"><span>Estimated total</span><span>{inr(rideTotal)}</span></div>
                          {isIntercity && <div className="flex justify-between text-brand"><span>Pay now — {payFull ? "full fare" : "10% booking deposit"}</span><span className="font-semibold">{inr(payFull ? rideTotal : Math.round(rideTotal * 0.1))}</span></div>}
                        </div>
                        <p className="mt-1.5 text-[11px] text-ink-muted">{isIntercity ? "Tolls and any waiting time are added by the driver on the trip and shown on your receipt. " : ""}Final fare may change with the actual route, traffic{isIntercity ? ", tolls" : ""} or waiting time.</p>
                      </div>
                    );
                  })()}
                  <div className="mt-5 space-y-3">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Tag className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
                        <Input value={promo} onChange={(e) => { setPromo(e.target.value); setPromoInfo(null); }} placeholder="Promo code (e.g. RIDE100)" className="h-11 bg-white pl-9 uppercase" data-testid="promo-input" />
                      </div>
                      <Button type="button" variant="outline" onClick={applyPromo} className="h-11 rounded-xl" data-testid="promo-apply">Apply</Button>
                    </div>
                    {promoInfo && <p className="text-xs font-medium text-graphite" data-testid="promo-applied">{promoInfo.code}: {inr(promoInfo.discount)} off · you pay {inr(promoInfo.final_amount)}</p>}
                    {!isIntercity && (
                    <div data-testid="ride-pay-select">
                      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted">Payment</p>
                      <div className="flex gap-2">
                        {[{ id: "cash", label: "Cash to driver" }, { id: "razorpay", label: "Pay online" }].map((m) => (
                          <button key={m.id} type="button" onClick={() => setPayMethod(m.id)} className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${payMethod === m.id ? "border-brand bg-brand-50 text-brand" : "border-surface-300 text-ink-soft hover:border-brand-200"}`} data-testid={`ride-pay-${m.id}`} aria-pressed={payMethod === m.id}>{m.label}</button>
                        ))}
                      </div>
                    </div>
                    )}
                    {pickup && (
                      <div className={`rounded-2xl border p-3 transition-all ${pinNudge ? "animate-pulse border-brand ring-2 ring-brand ring-offset-2" : pickupPinned ? "border-green-200 bg-green-50/40" : "border-amber-200 bg-amber-50/40"}`} data-testid="pickup-pin-section">
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink"><MapPin className="h-4 w-4 text-brand" /> Confirm exact pickup pin <span className="text-red-500">*</span></span>
                          <button type="button" onClick={() => setPinOpen((v) => !v)} className="text-xs font-semibold text-brand" data-testid="toggle-pickup-pin">{pinOpen ? "Hide map" : "Show map"}</button>
                        </div>
                        <p className="mt-1 text-xs text-ink-muted">Required before booking — drag the pin to your exact spot (within 300 m) and confirm.</p>
                        {pinOpen && (
                          <div className="mt-3">
                            <LocationPicker value={pickup} onChange={(p) => { setPickup({ ...pickup, lat: p.lat, lng: p.lng }); setPickupPinned(false); }} constrain={{ lat: pickupOrigin.current?.lat ?? pickup.lat, lng: pickupOrigin.current?.lng ?? pickup.lng, radiusM: 300 }} />
                          </div>
                        )}
                        <div className="mt-2 flex items-center justify-between gap-2">
                          {pickupPinned
                            ? <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700" data-testid="pickup-pin-confirmed"><MapPin className="h-3.5 w-3.5" /> Pickup pin confirmed</span>
                            : <span className="text-xs font-semibold text-amber-700">Pin not confirmed yet</span>}
                          <Button type="button" variant={pickupPinned ? "outline" : "default"} onClick={() => { if (distanceM(pickupOrigin.current, pickup) > 300) { setPinOpen(true); return toast.error("Pin must be within 300 m of your location"); } setPickupPinned(true); toast.success("Location confirmed"); }} className={`h-9 rounded-full font-bold ${pickupPinned ? "" : "bg-brand text-white hover:bg-brand-600"}`} data-testid="confirm-pickup-pin">{pickupPinned ? "Confirmed" : "Confirm Location"}</Button>
                        </div>
                      </div>
                    )}
                    {isIntercity && selected && (
                      <div className="rounded-2xl border border-brand-200 bg-brand-50 p-3" data-testid="intercity-fare-note">
                        <p className="flex items-center gap-1.5 text-sm font-semibold text-brand"><Info className="h-4 w-4" /> Long-distance (intercity) fare</p>
                        <div className="mt-2 space-y-1 text-xs text-ink-soft">
                          <div className="flex justify-between"><span>Distance</span><span className="font-medium text-ink">{estimate.distance_km} km{roundTrip ? " (round trip)" : ""}</span></div>
                          <div className="flex justify-between"><span>{selected.name} fare</span><span className="font-medium text-ink">{inr(selected.fare)}</span></div>
                          <div className="flex justify-between"><span>Booking deposit (10%)</span><span className="font-semibold text-brand">{inr(Math.round(selected.fare * 0.1))}</span></div>
                          {mode === "later" && <div className="flex justify-between"><span>When</span><span className="font-medium text-ink">{scheduledAt ? new Date(scheduledAt).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span></div>}
                        </div>
                        {mode !== "later" && (
                          <div className="mt-3" data-testid="intercity-deposit-choice">
                            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">Pay now online</p>
                            <div className="flex gap-2">
                              {[{ id: false, label: `10% deposit · ${inr(Math.round(selected.fare * 0.1))}` }, { id: true, label: `Full fare · ${inr(selected.fare)}` }].map((m) => (
                                <button key={String(m.id)} type="button" onClick={() => setPayFull(m.id)} className={`flex-1 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${payFull === m.id ? "border-brand bg-white text-brand shadow-sm" : "border-surface-300 bg-white/60 text-ink-soft hover:border-brand-200"}`} data-testid={`intercity-pay-${m.id ? "full" : "deposit"}`} aria-pressed={payFull === m.id}>{m.label}</button>
                              ))}
                            </div>
                          </div>
                        )}
                        <p className="mt-2 text-[11px] text-ink-muted">{mode === "later" ? "Estimate for the whole intercity trip." : "The 10% booking deposit is refunded only if no driver accepts (or the driver cancels). If you cancel, the deposit is non-refundable. It is adjusted into your final bill; if you pay the full fare and cancel, 90% is refunded and the 10% deposit is kept."} Final fare may change with the actual route, tolls or waiting time.</p>
                      </div>
                    )}
                    {acEligible && (
                      <div className="rounded-2xl border border-surface-300 bg-white p-3" data-testid="ac-addon">
                        <button type="button" onClick={() => setAc((v) => !v)} className="flex w-full items-center gap-3 text-left" data-testid="ac-toggle">
                          <Snowflake className={`h-5 w-5 shrink-0 ${ac ? "text-sky-500" : "text-ink-muted"}`} />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-bold text-ink">Air Conditioning</span>
                            <span className="mt-0.5 block text-xs text-ink-muted">+{acPct}% of the fare{acAmount ? ` · ${inr(acAmount)}` : ""}. Only AC cars get this request.</span>
                          </span>
                          <span className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${ac ? "bg-sky-500" : "bg-surface-300"}`}><span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${ac ? "left-[22px]" : "left-0.5"}`} /></span>
                        </button>
                        {ac && <p className="mt-2 rounded-lg bg-sky-50 px-3 py-2 text-[11px] leading-relaxed text-sky-700" data-testid="ac-note">The {acPct}% AC charge is calculated on the ride fare and shown here. If no AC car is nearby, you can choose to send it to all cars — then you won't be charged for AC. Final fare may still rise with traffic/waiting.</p>}
                      </div>
                    )}
                    <Button onClick={book} disabled={busy || !selected || !pickupPinned} className="h-12 w-full rounded-full bg-brand text-base font-bold text-white hover:bg-brand-600" data-testid="book-ride">
                      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      {mode === "later" ? "Schedule" : "Book"} {selected?.name || "ride"} {selected ? `· ${inr(rideTotal)}` : ""} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </Section>
              )}
              <Section title="Nearby" description={null}>
                <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="nearby-live-count">
                  <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" /></span>
                  <span className="text-sm font-bold text-ink">{drivers.length} driver{drivers.length === 1 ? "" : "s"} within 5 km right now</span>
                  {drivers.filter((d) => d.has_ac).length > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-bold text-sky-700" data-testid="nearby-ac-count"><Snowflake className="h-3 w-3" /> {drivers.filter((d) => d.has_ac).length} with AC</span>}
                </div>
                {heatPoints.length > 0 && (
                  <div className="mb-3" data-testid="driver-heatmap">
                    <RideMap pickup={pickup || DEFAULT_CENTER} heatmapPoints={heatPoints} className="min-h-[200px]" />
                    <p className="mt-1.5 text-[11px] text-ink-muted">Shaded zones show where drivers are right now — pick a pickup point near them to avoid a far-pickup fee.</p>
                  </div>
                )}
                <div className="space-y-2" data-testid="nearby-drivers">
                  {drivers.slice(0, 5).map((d, i) => (
                    <div key={d.driver_id || d.id || i} className="flex items-center gap-3 rounded-2xl border border-surface-300 bg-white p-3" data-testid={`nearby-${i}`}>
                      {d.car_photos?.length ? (
                        <img src={privateCarApi.photoUrl(d.car_photos[0])} alt={d.vehicle_name || "Vehicle"} className="h-12 w-14 shrink-0 rounded-xl object-cover" data-testid={`nearby-car-${i}`} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : d.photo ? (
                        <img src={d.photo} alt={d.name || "Driver"} className="h-11 w-11 shrink-0 rounded-full object-cover" data-testid={`nearby-photo-${i}`} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand"><Car className="h-5 w-5" /></span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-ink" data-testid={`nearby-name-${i}`}>{d.name || "Driver nearby"}</p>
                        <p className="text-xs text-ink-soft">{d.vehicle_name || VEHICLE_LABELS[d.vehicle_type] || d.vehicle_type || "Driver"}{[d.distance_km != null ? ` · ${d.distance_km} km away` : "", d.eta_min != null ? ` · ~${d.eta_min} min` : ""].join("")}</p>
                      </div>
                      {d.has_ac && <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-bold text-sky-700" data-testid={`nearby-ac-${i}`}><Snowflake className="h-3 w-3" /> AC</span>}
                      {d.verified && <span className="shrink-0 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-bold text-green-700">Verified</span>}
                    </div>
                  ))}
                  {drivers.length === 0 && <p className="text-sm text-ink-muted">No drivers within 5 km yet — hang tight.</p>}
                  <Link to="/app/drivers-near-me" className="inline-flex items-center gap-1 pt-1 text-sm font-semibold text-brand hover:text-brand-600" data-testid="see-all-nearby">See all nearby <ArrowRight className="h-4 w-4" /></Link>
                </div>
              </Section>
            </>
          )}
        </div>
        {ride && ride.status !== "cancelled" ? (
          <div className="space-y-3">
            <DriverEtaBar ride={ride} />
            {Number.isFinite(ride.drop_lat) && ["driver_assigned", "driver_arriving", "in_progress"].includes(ride.status) && (
              <Button onClick={() => setNavDest({ lat: ride.drop_lat, lng: ride.drop_lng })} className="w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="customer-navigate-inapp"><Navigation className="mr-2 h-4 w-4" /> Navigate the route (in-app)</Button>
            )}
            <div className="relative">
              <LiveRideMap ride={ride} drivers={["pending", "searching", "dispatching", "requested", "finding_driver"].includes(ride.status) ? drivers : undefined} className="min-h-[420px] lg:min-h-[620px]" />
              <Link to="/app/trip" className="absolute bottom-4 right-4 z-[500] inline-flex items-center gap-1.5 rounded-full bg-graphite px-3.5 py-2 text-sm font-semibold text-white shadow-lg shadow-ink/20 hover:bg-graphite-600" data-testid="open-full-trip">
                <Maximize2 className="h-4 w-4" /> Full screen
              </Link>
            </div>
          </div>
        ) : null}
      </div>
      <CancelReasonDialog open={cancelOpen} onOpenChange={setCancelOpen} onConfirm={doCancelRide} fee={ride?.payment_status === "paid"} />
      {navDest && <InAppNav destination={navDest} label="drop-off" onExit={() => setNavDest(null)} />}
    </>
  );
}
